import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import ReactSVG from 'react-svg'
import { NavLink } from 'react-router-dom'

import { useGetWatchLocation } from '../../hooks/useGetWatchLocation'
import styles from './watch-button.css'
import { DeepLink } from '../../hoc/deep-link'
import ConfirmationModal from '../shared/confirmation-modal'
import { KIDS_PROFILE_ADD_SUBSCRIPTION_ERROR_MESSAGE, KIDS_PROFILE_PURCHASE_ERROR_MESSAGE } from '../../constants'
import playIcon from '../../../images/new-play.svg'
import Button from '../button'

function WatchButton({
  ctaPrefix,
  ctaSuffix,
  price,
  displayPrice,
  onClick,
  clickable,
  watchUrl,
  className,
  skipIntro,
  isRental,
  isKid,
  watchable,
  progressPercentage,
  isComingSoon,
  rentalNote,
  svodMoviePath
}) {
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const clickableClass = clickable ? styles.clickable : styles.notClickable
  const watchLocation = useGetWatchLocation(watchUrl, skipIntro, isKid, watchable)

  const handleOnClick = (event) => {
    if (isKid && !watchable) {
      setShowErrorMessage(true)
    }
    // Pass event into onClick to handle the event.preventDefault() for chromecast
    onClick(event)
  }

  const onConfirmClick = () => {
    setShowErrorMessage(false)
  }

  if (showErrorMessage) {
    return (
      <ConfirmationModal
        header="Hmmm"
        message={isRental ? KIDS_PROFILE_PURCHASE_ERROR_MESSAGE : KIDS_PROFILE_ADD_SUBSCRIPTION_ERROR_MESSAGE}
        confirmText="OK"
        onConfirmClick={onConfirmClick}
        customWrapperClass={styles.errorMessageWrapper}
      />
    )
  }

  // REMOVE ME, this is for outlander issue only, remove after season 5 is out
  if (!watchUrl && /\/series\/outlander/.test(location.pathname)) {
    return null
  }

  // Get button Text
  const buttonText = () => {
    if (isComingSoon) {
      return 'COMING SOON'
    }

    return (
      `${ctaPrefix}
      ${ctaSuffix ? ` ${ctaSuffix}` : ''}
      ${displayPrice ? ` ${price}` : ''}`
    )
  }

  return (
    <div
      className={classNames(
        styles.buttonWrapper,
        clickableClass
      )}
    >
      <DeepLink
        isPlayButton
        to={watchLocation}
        onClick={handleOnClick}
        className={classNames(
          clickableClass,
          className,
          styles.link
        )}
      >
        <Button
          wrapperClassName={styles.buttonWrapper}
          className={styles.button}
          disabled={!clickable || isComingSoon}
          isRental={isRental}
          e2eIdentifier="watch-button-on-cta-box"
        >
          <div className={styles.buttonTextStyle}>
            <ReactSVG
              src={playIcon}
              beforeInjection={(svg) => {
                const path = svg.querySelector('path')
                if (path) {
                  if (isComingSoon) {
                    // Set different color when content is coming soon (disabled button)
                    path.setAttribute('fill', '#525252')
                  } else {
                    path.setAttribute('fill', 'black')
                  }
                }
              }}
            />

            {buttonText()}

            {/* Only display the progressPercentage if it is greater than 0 */}
            {progressPercentage
              ? (
                <div className={styles.progressTube}>
                  <div className={styles.progressBar} style={{ width: `${progressPercentage}%` }} />
                </div>
              ) : null}
          </div>
        </Button>
      </DeepLink>

      {(svodMoviePath && rentalNote) && (
        <NavLink
          to={svodMoviePath}
          className={styles.rentalNote}
        >
          {rentalNote}
        </NavLink>
      )}
    </div>
  )
}

WatchButton.propTypes = {
  watchUrl: PropTypes.oneOfType([
    PropTypes.oneOfType([PropTypes.object]),
    PropTypes.string
  ]),
  ctaPrefix: PropTypes.string,
  ctaSuffix: PropTypes.string,
  price: PropTypes.string,
  displayPrice: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  clickable: PropTypes.bool,
  className: PropTypes.string,
  skipIntro: PropTypes.bool,
  isRental: PropTypes.bool.isRequired,
  isKid: PropTypes.bool,
  watchable: PropTypes.bool,
  progressPercentage: PropTypes.number,
  isComingSoon: PropTypes.bool,
  rentalNote: PropTypes.string,
  svodMoviePath: PropTypes.string
}

WatchButton.defaultProps = {
  ctaPrefix: '',
  ctaSuffix: '',
  price: '',
  clickable: true,
  onClick: () => { },
  watchUrl: '#',
  className: '',
  skipIntro: false,
  isRental: false,
  isKid: false,
  watchable: false,
  progressPercentage: 0,
  isComingSoon: false,
  rentalNote: null,
  svodMoviePath: null
}

export default WatchButton
